// KpiGridRow yardımcı parçaları (≤600 satır kuralı — Dalga 3'te KpiGridRow.tsx'ten ayrıldı):
// bant istatistiği, portal hücre altı durum rozeti / "danışman düzeltti" notu, sağ durum sütunu ve
// Y4a YAPI SATIRI (değersiz iskelet bant: kapalı kategori, görünür torunu var; hücre `<td>` yok,
// düzenlenemez, not rozeti yok, sayaç yok — müşteri kapalı kategori hakkında sayı görmez) ve aynı
// bileşenin `group` biçimi: kırılım grubu bandı (spec §18 DR1/DR8).
// K8: kısmi toplam (†) hücresinin ⓘ düğmesi + yerel pencere durumu (`PartialInfoButton`).

import { useRef, useState } from "react";
import { type KpiResolvedCell } from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import type { GridColumn, GridRow } from "../../lib/kpi-grid-nav";
import { gridRowHeight, readCell, rowIndent } from "../../lib/kpi-grid-nav";
import {
  partialInfoFor,
  partialInfoKeyStops,
  type PartialLookup,
} from "../../lib/kpi-partial-info";
import { KPI_COPY } from "../../lib/kpi-ui";
import { PartialInfoPopover } from "./PartialInfoPopover";
import {
  type ClientStatusBadge,
  clientStatusBadge,
  correctionLine,
  rowStatusCounts,
  statusSummary,
} from "../../lib/portal-kpi-ui";

export const TD_BORDER = "1px solid var(--color-border)";

export function BandStats({ s }: { s: NonNullable<GridRow["bandStats"]> }): JSX.Element {
  return (
    <span className="ml-2 text-xs" style={{ color: "var(--color-text-3)", fontWeight: 400 }}>
      {KPI_COPY.grid.bandStats(s)}
    </span>
  );
}

/** Hücre altı durum rozeti (DR4) — tek stil kaynağı. */
function StatusBadge({ badge }: { badge: ClientStatusBadge }): JSX.Element {
  const tone: React.CSSProperties =
    badge.tone === "await"
      ? {
          background: "var(--color-warning-bg)",
          color: "var(--color-warning-text)",
          border: "1px solid var(--color-warning-border)",
        }
      : badge.tone === "verified"
        ? {
            background: "var(--kpi-verified-bg)",
            color: "var(--kpi-verified-text)",
            border: "1px solid var(--kpi-verified-border)",
          }
        : badge.tone === "clarify"
          ? {
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
              border: "1px solid transparent",
            }
          : {};
  if (badge.pill) {
    return (
      <span
        style={{
          display: "inline-block",
          padding: "1px 8px",
          borderRadius: 999,
          fontSize: "0.66rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
          ...tone,
        }}
      >
        {badge.text}
      </span>
    );
  }
  return (
    <span style={badge.tone === "dot" ? { color: "var(--color-warning-text)" } : undefined}>
      {badge.tone === "dot" ? (
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "var(--color-warning-text)",
            marginRight: 5,
            verticalAlign: "middle",
          }}
        />
      ) : null}
      {badge.text}
    </span>
  );
}

/**
 * Finar 10.5 (K1) — portal hücre altı: durum rozeti + "danışman düzeltti" notu ilgili YILIN
 * hücresinde. Boş/salt-okunur hücrede satır durumu ("boş", "danışman girer") tekrarlanmaz.
 */
export function CellStatusNote({
  ind,
  cell,
}: {
  ind: GridRow["indicator"];
  cell: KpiResolvedCell | undefined;
}): JSX.Element | null {
  const badge = clientStatusBadge(ind, cell);
  const corrected = correctionLine(cell, { percent: ind.valueType === "percent" });
  if (!badge.pill && !corrected) return null;
  return (
    <div
      data-testid="kpi-cell-status"
      style={{ marginTop: 3, fontSize: "0.72rem", color: "var(--color-text-3)", lineHeight: 1.25 }}
    >
      {badge.pill ? <StatusBadge badge={badge} /> : null}
      {corrected ? <div style={{ whiteSpace: "normal" }}>{corrected}</div> : null}
    </div>
  );
}

/** Sağ sütun: görünür yıllardaki durumların özeti ("1 danışman onayında · 1 düzeltildi"). */
export function ClientStatusCell({
  row,
  columns,
  sticky,
}: {
  row: GridRow;
  columns: GridColumn[];
  sticky: boolean;
}): JSX.Element {
  const ind = row.indicator;
  const cells = columns.filter((c) => !c.aggregate).map((c) => readCell(ind, c));
  const text = statusSummary(rowStatusCounts(ind, cells));
  return (
    <td
      data-testid="kpi-client-status"
      style={{
        padding: "6px 10px",
        borderBottom: TD_BORDER,
        borderLeft: TD_BORDER,
        fontSize: "0.78rem",
        whiteSpace: "nowrap",
        color: "var(--color-text-3)",
        background: "var(--color-surface)",
        ...(sticky ? { position: "sticky", right: 0, zIndex: 1 } : {}),
      }}
    >
      {text}
    </td>
  );
}

/**
 * Hücresiz geniş satır — iki biçim (`StructuralRow` parametreli, DR1/DR8):
 *  · `category` — Y4a yapı satırı: yalnız kategori adı (sade bant), 0.85rem/700 `--color-primary`,
 *    aç/kapa düğmesi (`aria-expanded={open}`) kalır (kapalı kategorinin görünür çocuklarını gizler);
 *  · `group` — kırılım grubu bandı (sessiz ayırıcı): 0.75rem/600 `--color-text-3`, 1 px üst kenar
 *    `--color-border` %40, etiket + rol eki (`· hesap grubu` yalnız danışman) + `(seçili …)`;
 *    sayı YOK; etiket aç/kapa düğmesi (`aria-expanded`, TODO-KPI-124); satır `aria-level` (treegrid); 36 px (portal 40).
 * İkisinde de `<td>`/`gridcell` YOK — başlık hücresi (`<th scope="row">`) tüm satırı kaplar.
 */
export function StructuralRow(props: {
  row: GridRow;
  rowIndex: number;
  colSpan: number;
  onToggleBand: (id: string) => void;
  clientView: boolean;
  flash: boolean;
  variant?: "category" | "group";
  narrow?: boolean | undefined;
}): JSX.Element {
  const { row, rowIndex, colSpan, onToggleBand, clientView, flash } = props;
  const variant = props.variant ?? "category";
  const open = row.open !== false;
  const group = variant === "group";
  const copy = clientView ? KPI_BREAKDOWN_COPY.portal : KPI_BREAKDOWN_COPY;
  return (
    <tr
      aria-rowindex={rowIndex + 1}
      {...row.tree}
      data-testid={group ? "kpi-group-band" : "kpi-structural-row"}
      style={{
        background: flash
          ? "color-mix(in srgb, var(--color-primary) 12%, white)"
          : group
            ? "var(--color-surface)"
            : "var(--color-bg)",
        height: gridRowHeight(clientView, group ? "group" : "band"),
      }}
    >
      <th
        scope="row"
        colSpan={colSpan}
        title={group && props.narrow ? row.indicator.label : undefined}
        style={{
          textAlign: "left",
          // grup bandı: dikey boşluk yok — aç/kapa düğmesi (28/44 px hedef) satır yüksekliğini (36/40)
          // itmesin; portalda 44 px düğme negatif kenar boşluğuyla 40 px bandın içine sığar
          padding: group ? "0 10px" : "7px 10px",
          paddingLeft: rowIndent(row.depth, !!props.narrow),
          borderBottom: TD_BORDER,
          ...(group
            ? {
                borderTop: "1px solid color-mix(in srgb, var(--color-border) 40%, transparent)",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "var(--color-text-3)",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
            : { fontSize: "0.85rem", fontWeight: 700, color: "var(--color-primary)" }),
          whiteSpace: "nowrap",
        }}
      >
        {group ? (
          <>
            {/* TODO-KPI-124: grup bandı aç/kapanır (native düğme: Tab + Enter/Space) */}
            <button
              type="button"
              aria-expanded={open}
              aria-label={copy.toggle(open, row.indicator.label)}
              onClick={() => onToggleBand(row.indicator.id)}
              style={{
                color: "inherit",
                fontWeight: 600,
                minHeight: clientView ? 44 : 28,
                minWidth: clientView ? 44 : undefined,
                margin: clientView ? "-3px 0" : undefined,
              }}
            >
              <span aria-hidden="true" style={{ marginRight: 6 }}>
                {open ? "▾" : "▸"}
              </span>
              {row.indicator.label}
            </button>
            {row.group?.calc && !clientView ? (
              <span style={{ fontWeight: 400 }}> {KPI_BREAKDOWN_COPY.calcGroupSuffix}</span>
            ) : null}
            {row.group?.complete === false ? (
              <span style={{ fontWeight: 400 }}> {copy.partialGroup}</span>
            ) : null}
          </>
        ) : (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => onToggleBand(row.indicator.id)}
            style={{ color: "inherit", fontWeight: 700, minHeight: 28 }}
          >
            <span aria-hidden="true" style={{ marginRight: 6 }}>
              {open ? "▾" : "▸"}
            </span>
            {row.indicator.label}
          </button>
        )}
      </th>
    </tr>
  );
}

/**
 * Kırılım toplamının ad hücresi içeriği (DR2/DR8/DR9): ▾/▸ düğmesi (`aria-expanded={open}`, ok
 * `--color-text-3`; portalda ≥44 px dokunma hedefi) + 0.85rem/700 `--color-text` etiket (mavi değil —
 * kategori bandından ayrışır). Grid'de pill YOK; yalnız kapalıyken "· 2 kırılım" (0.75rem
 * `--color-text-3`; dar düzende yalnız sayı; portalda "· N alt kalem").
 */
export function TotalName(props: {
  row: GridRow;
  clientView: boolean;
  narrow: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { row, clientView, narrow, onToggle } = props;
  const open = row.open !== false;
  const info = row.total!;
  const copy = clientView ? KPI_BREAKDOWN_COPY.portal : KPI_BREAKDOWN_COPY;
  const count = clientView ? info.memberCount : info.groups.length;
  const touch = clientView ? 44 : 28;
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-label={copy.toggle(open, row.indicator.label)}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        style={{
          color: "var(--color-text-3)",
          minWidth: clientView ? 44 : 16,
          minHeight: touch,
          marginRight: 4,
          flexShrink: 0,
        }}
      >
        <span aria-hidden="true">{open ? "▾" : "▸"}</span>
      </button>
      <span
        className="truncate min-w-0"
        data-testid="kpi-total-label"
        style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text)" }}
      >
        {row.member?.label ?? row.indicator.label}
      </span>
      {!open ? (
        <span
          className="ml-1"
          data-testid="kpi-total-count"
          style={{ fontSize: "0.75rem", color: "var(--color-text-3)", flexShrink: 0 }}
        >
          {narrow ? copy.collapsedCountNarrow(count) : copy.collapsedCount(count)}
        </span>
      ) : null}
    </>
  );
}

/**
 * K8 (Finar) — kısmi toplam hücresinde amber noktanın yanındaki ⓘ. Tıklama ve etkinleştirme
 * tuşları (Enter/Space) hücreye KÖPÜRMEZ: hücre editörü ya da formül penceresi açılmaz. Pencere durumu yerel
 * (KpiGrid ≤600 satır); kapanınca odak ⓘ'ye döner.
 */
export function PartialInfoButton(p: {
  cell: KpiResolvedCell;
  lookup: PartialLookup;
  client: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const copy = p.client ? KPI_COPY.portal : KPI_COPY.cell;
  const info = open ? partialInfoFor(p.cell, p.lookup, { client: p.client }) : null;
  const stop = (e: React.SyntheticEvent): void => e.stopPropagation();
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid="kpi-partial-info-btn"
        aria-label={copy.partialInfo}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={copy.partialInfo}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onDoubleClick={stop}
        onKeyDown={(e) => {
          // yalnız Enter/Space ve açıkken Escape yutulur; oklar/Tab ızgara gezinmesine köpürür
          if (!partialInfoKeyStops(e.key, open)) return;
          e.stopPropagation();
          if (e.key === "Escape") setOpen(false);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 20,
          minHeight: 20,
          marginLeft: 2,
          verticalAlign: "middle",
          fontSize: "0.75rem",
          lineHeight: 1,
          color: "var(--color-warning-text)",
          cursor: "pointer",
        }}
      >
        ⓘ
      </button>
      {info ? (
        <PartialInfoPopover
          title={copy.partialTitle}
          refs={info.refs}
          entities={info.entities}
          hasPending={info.hasPending}
          hiddenInputs={info.refs.filter((r) => r.hidden).map((r) => r.code)}
          client={p.client}
          getAnchor={() => btnRef.current}
          onClose={() => {
            setOpen(false);
            btnRef.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}
